import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiPlus,
  FiGrid,
  FiList,
  FiColumns,
  FiSearch,
  FiUser,
  FiMail,
  FiPhone,
  FiBriefcase,
  FiCalendar,
  FiCheck,
  FiX,
  FiClock,
  FiTrendingUp,
  FiTrendingDown,
  FiActivity
} from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import { Modal } from '../../components/UI/Modal';
import { prospectService } from '../../services';
import { useDebounce } from '../../hooks/useDebounce';
import './Prospeccion.scss';

const Prospeccion = () => {
  const navigate = useNavigate();

  // Estados principales
  const [prospects, setProspects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('todos'); // todos, en_proceso, alcanzados, perdidos
  const [error, setError] = useState(null);

  // Estados de estadísticas
  const [stats, setStats] = useState({
    total: 0,
    en_proceso: 0,
    alcanzados: 0,
    perdidos: 0,
    actividades_seguimiento: 0
  });

  // Modales
  const [showAddModal, setShowAddModal] = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Form data para nuevo/editar prospecto
  const [prospectFormData, setProspectFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    business_name: '',
    client_type: 'betel_natural_tienda',
    status: 'en_proceso',
    assigned_user_id: 1, // TODO: Obtener del usuario actual
    status_date: new Date().toISOString().split('T')[0],
    status_reason: '',
    notes: '',
    source: '',
    tags: ''
  });

  // Form data para programar seguimiento
  const [followUpFormData, setFollowUpFormData] = useState({
    activity_type: 'follow_up',
    title: '',
    description: '',
    next_follow_up_date: ''
  });

  // Opciones para dropdowns
  const [options, setOptions] = useState({
    clientTypes: [
      { value: 'betel_natural_tienda', label: 'Betel Natural Tienda' },
      { value: 'betel_natural_distribuidor', label: 'Betel Natural Distribuidor' },
      { value: 'naturalandlab', label: 'Naturaland Lab' }
    ],
    statuses: [
      { value: 'en_proceso', label: 'En Proceso' },
      { value: 'alcanzado', label: 'Alcanzado' },
      { value: 'perdido', label: 'Perdido' }
    ],
    activityTypes: [
      { value: 'call', label: 'Llamada' },
      { value: 'email', label: 'Email' },
      { value: 'meeting', label: 'Reunión' },
      { value: 'note', label: 'Nota' },
      { value: 'follow_up', label: 'Seguimiento' },
      { value: 'other', label: 'Otro' }
    ]
  });

  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  // Cargar datos iniciales
  useEffect(() => {
    loadProspects();
    loadStats();
    loadOptions();
  }, [activeTab]);

  // Búsqueda con debounce
  useEffect(() => {
    if (debouncedSearchQuery) {
      performSearch(debouncedSearchQuery);
    } else {
      loadProspects();
    }
  }, [debouncedSearchQuery]);

  // ==================== FUNCIONES DE CARGA ====================

  const loadProspects = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      let result;
      const baseParams = {
        skip: 0,
        limit: 100,
        is_active: true
      };

      // Filtrar según la pestaña activa
      if (activeTab !== 'todos') {
        baseParams.status = activeTab;
      }

      result = await prospectService.getProspects(baseParams);

      if (result.success) {
        const prospectsData = result.data || [];
        setProspects(prospectsData);
      } else {
        setError(result.message || 'Error al cargar prospectos');
      }
    } catch (error) {
      console.error('Error cargando prospectos:', error);
      setError('Error al cargar prospectos');
    } finally {
      setIsLoading(false);
    }
  }, [activeTab]);

  const loadStats = async () => {
    try {
      const result = await prospectService.getStats();
      if (result.success && result.data) {
        setStats(result.data);
      }
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
    }
  };

  const loadOptions = async () => {
    try {
      const result = await prospectService.getOptions();
      if (result.success && result.data) {
        setOptions({
          clientTypes: result.data.client_types || options.clientTypes,
          statuses: result.data.statuses || options.statuses,
          activityTypes: result.data.activity_types || options.activityTypes
        });
      }
    } catch (error) {
      console.error('Error cargando opciones:', error);
    }
  };

  const performSearch = async (query) => {
    if (!query.trim()) {
      loadProspects();
      return;
    }

    setIsLoading(true);
    try {
      const params = {
        skip: 0,
        limit: 100,
        status: activeTab !== 'todos' ? activeTab : undefined
      };

      const result = await prospectService.searchProspects(query, params);
      if (result.success) {
        setProspects(result.data || []);
      }
    } catch (error) {
      console.error('Error en búsqueda:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // ==================== HANDLERS DE MODALES ====================

  const handleOpenAddModal = () => {
    navigate('/system/seguimiento/nuevo');
  };

  const handleOpenEditModal = (prospect) => {
    navigate(`/system/seguimiento/editar/${prospect.id}`);
  };

  const handleOpenFollowUpModal = (prospect) => {
    setSelectedProspect(prospect);
    setFollowUpFormData({
      activity_type: 'follow_up',
      title: '',
      description: '',
      next_follow_up_date: ''
    });
    setShowFollowUpModal(true);
  };

  // ==================== HANDLERS DE FORMULARIOS ====================

  const handleSaveProspect = async () => {
    try {
      let result;

      if (isEditing && selectedProspect) {
        result = await prospectService.updateProspect(selectedProspect.id, prospectFormData);
      } else {
        result = await prospectService.createProspect(prospectFormData);
      }

      if (result.success) {
        setShowAddModal(false);
        loadProspects();
        loadStats();
        alert(isEditing ? 'Prospecto actualizado exitosamente' : 'Prospecto creado exitosamente');
      } else {
        alert(result.message || 'Error al guardar prospecto');
      }
    } catch (error) {
      console.error('Error guardando prospecto:', error);
      alert('Error al guardar prospecto');
    }
  };

  const handleSaveFollowUp = async () => {
    if (!selectedProspect) return;

    try {
      const activityData = {
        ...followUpFormData,
        prospect_id: selectedProspect.id
      };

      const result = await prospectService.addActivity(selectedProspect.id, activityData);

      if (result.success) {
        setShowFollowUpModal(false);
        loadProspects();
        loadStats();
        alert('Seguimiento programado exitosamente');
      } else {
        alert(result.message || 'Error al programar seguimiento');
      }
    } catch (error) {
      console.error('Error programando seguimiento:', error);
      alert('Error al programar seguimiento');
    }
  };

  const handleDeleteProspect = async (prospectId) => {
    if (!window.confirm('¿Está seguro de eliminar este prospecto?')) {
      return;
    }

    try {
      const result = await prospectService.deleteProspect(prospectId);
      if (result.success) {
        loadProspects();
        loadStats();
        alert('Prospecto eliminado exitosamente');
      } else {
        alert(result.message || 'Error al eliminar prospecto');
      }
    } catch (error) {
      console.error('Error eliminando prospecto:', error);
      alert('Error al eliminar prospecto');
    }
  };

  // ==================== HELPERS ====================

  const getStatusColor = (status) => {
    const colors = {
      en_proceso: 'orange',
      alcanzado: 'green',
      perdido: 'red'
    };
    return colors[status] || 'gray';
  };

  const getStatusLabel = (status) => {
    const labels = {
      en_proceso: 'EN PROCESO',
      alcanzado: 'ALCANZADO',
      perdido: 'PERDIDO'
    };
    return labels[status] || status?.toUpperCase();
  };

  const getClientTypeLabel = (type) => {
    const option = options.clientTypes.find(o => o.value === type);
    return option ? option.label : type;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (isLoading && prospects.length === 0) {
    return <LoadingGrid message="Cargando prospectos..." />;
  }

  return (
    <div className="prospeccion-page">
      {/* Error message */}
      {error && (
        <div className="alert alert--danger">
          <FiUser className="alert-icon" />
          <div className="alert-content">
            <strong>Error:</strong> {error}
          </div>
        </div>
      )}

      {/* Título */}
      <h1 className="page-title">SEGUIMIENTO DE NUEVOS CLIENTES</h1>

      {/* Menú de acciones */}
      <div className="page-header-with-menu">
        <div className="action-menu">
          <button className="menu-item menu-item--first" onClick={handleOpenAddModal}>
            + AGREGAR PROSPECTO
          </button>

          <div className="menu-item menu-item--search">
            <label className="search-label">BUSCAR:</label>
            <input
              type="text"
              className="search-input"
              placeholder="NOMBRE DEL CLIENTE"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="menu-item menu-item--views">
            <button
              className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Vista de tarjetas"
            >
              <FiGrid />
            </button>
            <button
              className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title="Vista de lista"
            >
              <FiList />
            </button>
            <button
              className={`view-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
              title="Vista de tabla"
            >
              <FiColumns />
            </button>
          </div>
        </div>
      </div>

      {/* Tabs de estado de prospectos */}
      <div className="prospect-tabs">
        <button
          className={`prospect-tab ${activeTab === 'todos' ? 'active' : ''}`}
          onClick={() => setActiveTab('todos')}
        >
          <FiUser /> TODOS LOS PROSPECTOS
        </button>
        <button
          className={`prospect-tab prospect-tab--orange ${activeTab === 'en_proceso' ? 'active' : ''}`}
          onClick={() => setActiveTab('en_proceso')}
        >
          <FiClock /> EN PROCESO
        </button>
        <button
          className={`prospect-tab prospect-tab--green ${activeTab === 'alcanzado' ? 'active' : ''}`}
          onClick={() => setActiveTab('alcanzado')}
        >
          <FiTrendingUp /> ALCANZADOS
        </button>
        <button
          className={`prospect-tab prospect-tab--red ${activeTab === 'perdido' ? 'active' : ''}`}
          onClick={() => setActiveTab('perdido')}
        >
          <FiTrendingDown /> PERDIDOS
        </button>
      </div>

      {/* Contenido de prospectos */}
      {prospects.length === 0 && !isLoading ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <FiUser size={48} />
          </div>
          <h3 className="empty-state-title">
            {searchQuery ? 'No se encontraron prospectos' : 'No hay prospectos registrados'}
          </h3>
          <p className="empty-state-description">
            {searchQuery
              ? `No se encontraron prospectos que coincidan con "${searchQuery}"`
              : 'Comienza agregando tu primer prospecto'
            }
          </p>
        </div>
      ) : viewMode === 'table' ? (
        <div className="table-container">
          <table className="data-table data-table--prospectos">
            <thead>
              <tr>
                <th>NOMBRE</th>
                <th>EMAIL</th>
                <th>TELÉFONO</th>
                <th className="text-center">ESTADO</th>
              </tr>
            </thead>
            <tbody>
              {prospects.map((prospect) => (
                <tr
                  key={prospect.id}
                  onClick={() => handleOpenEditModal(prospect)}
                  style={{ cursor: 'pointer' }}
                >
                  <td><strong>{prospect.full_name}</strong></td>
                  <td>{prospect.email}</td>
                  <td>{prospect.phone || 'Sin teléfono'}</td>
                  <td className="text-center">
                    <span className={`status-badge status-${prospect.status}`}>
                      {getStatusLabel(prospect.status)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className={viewMode === 'grid' ? 'content-grid' : 'content-list'}>
          {prospects.map((prospect) => (
            <div
              key={prospect.id}
              className={viewMode === 'grid' ? 'content-card' : 'content-card-list'}
              onClick={() => handleOpenEditModal(prospect)}
              style={{ cursor: 'pointer' }}
            >
              {viewMode === 'grid' ? (
                <>
                  <div className="content-thumbnail blue-dark-gradient"></div>
                  <div className="content-info">
                    <div className="content-title">{prospect.full_name}</div>
                    <div className="content-subtitle">{prospect.email}</div>
                    <div className="content-meta-grid">
                      <span className="meta-item">{prospect.phone || 'Sin teléfono'}</span>
                      <span className={`meta-item status-badge status-${prospect.status}`}>{getStatusLabel(prospect.status)}</span>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="content-thumbnail-small blue-dark-gradient"></div>
                  <div className="content-info-list">
                    <div className="content-title">{prospect.full_name}</div>
                    <div className="content-subtitle">{prospect.email}</div>
                    <div className="content-meta">
                      <span className="meta-item">{prospect.phone || 'Sin teléfono'}</span>
                      <span className={`meta-item status-badge status-${prospect.status}`}>{getStatusLabel(prospect.status)}</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal: Agregar/Editar Prospecto (DAR DE ALTA del PDF) */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title={isEditing ? 'Editar Prospecto' : 'Agregar Prospecto'}
        size="large"
      >
        <div className="modal-body">
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Nombre del Cliente *</label>
              <input
                type="text"
                className="form-input"
                value={prospectFormData.full_name}
                onChange={(e) => setProspectFormData({ ...prospectFormData, full_name: e.target.value })}
                placeholder="Juan Pérez"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Email *</label>
              <input
                type="email"
                className="form-input"
                value={prospectFormData.email}
                onChange={(e) => setProspectFormData({ ...prospectFormData, email: e.target.value })}
                placeholder="correo@dominio.com"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Teléfono</label>
              <input
                type="tel"
                className="form-input"
                value={prospectFormData.phone}
                onChange={(e) => setProspectFormData({ ...prospectFormData, phone: e.target.value })}
                placeholder="+1 (000)-000-0000"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Nombre del Negocio</label>
              <input
                type="text"
                className="form-input"
                value={prospectFormData.business_name}
                onChange={(e) => setProspectFormData({ ...prospectFormData, business_name: e.target.value })}
                placeholder="Distribuidora Ejemplo"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Tipo de Cliente *</label>
              <select
                className="form-input"
                value={prospectFormData.client_type}
                onChange={(e) => setProspectFormData({ ...prospectFormData, client_type: e.target.value })}
              >
                {options.clientTypes.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Status del Proceso *</label>
              <select
                className="form-input"
                value={prospectFormData.status}
                onChange={(e) => setProspectFormData({ ...prospectFormData, status: e.target.value })}
              >
                {options.statuses.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Fecha</label>
              <input
                type="date"
                className="form-input"
                value={prospectFormData.status_date}
                onChange={(e) => setProspectFormData({ ...prospectFormData, status_date: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Origen</label>
              <input
                type="text"
                className="form-input"
                value={prospectFormData.source}
                onChange={(e) => setProspectFormData({ ...prospectFormData, source: e.target.value })}
                placeholder="Referencia, web, etc."
              />
            </div>

            {prospectFormData.status === 'perdido' && (
              <div className="form-group form-group--full">
                <label className="form-label">Razón del Status *</label>
                <textarea
                  className="form-input"
                  value={prospectFormData.status_reason}
                  onChange={(e) => setProspectFormData({ ...prospectFormData, status_reason: e.target.value })}
                  placeholder="Explique por qué se perdió el prospecto"
                  rows={3}
                />
              </div>
            )}

            <div className="form-group form-group--full">
              <label className="form-label">Notas</label>
              <textarea
                className="form-input"
                value={prospectFormData.notes}
                onChange={(e) => setProspectFormData({ ...prospectFormData, notes: e.target.value })}
                placeholder="Notas adicionales sobre el prospecto"
                rows={4}
              />
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button
            className="btn btn--outline"
            onClick={() => setShowAddModal(false)}
          >
            <FiX size={18} />
            <span>Cancelar</span>
          </button>
          <button
            className="btn btn--primary"
            onClick={handleSaveProspect}
            disabled={!prospectFormData.full_name || !prospectFormData.email}
          >
            <FiCheck size={18} />
            <span>{isEditing ? 'Actualizar' : 'Dar de Alta'}</span>
          </button>
        </div>
      </Modal>

      {/* Modal: Programar Seguimiento */}
      <Modal
        isOpen={showFollowUpModal}
        onClose={() => setShowFollowUpModal(false)}
        title="Programar Seguimiento"
        size="medium"
      >
        <div className="modal-body">
          {selectedProspect && (
            <div className="followup-info-summary">
              <p><strong>Prospecto:</strong> {selectedProspect.full_name}</p>
              <p><strong>Email:</strong> {selectedProspect.email}</p>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Tipo de Actividad *</label>
            <select
              className="form-input"
              value={followUpFormData.activity_type}
              onChange={(e) => setFollowUpFormData({ ...followUpFormData, activity_type: e.target.value })}
            >
              {options.activityTypes.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Título *</label>
            <input
              type="text"
              className="form-input"
              value={followUpFormData.title}
              onChange={(e) => setFollowUpFormData({ ...followUpFormData, title: e.target.value })}
              placeholder="Ej: Llamada de seguimiento semanal"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Descripción</label>
            <textarea
              className="form-input"
              value={followUpFormData.description}
              onChange={(e) => setFollowUpFormData({ ...followUpFormData, description: e.target.value })}
              placeholder="Detalles de la actividad"
              rows={4}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Próximo Seguimiento</label>
            <input
              type="date"
              className="form-input"
              value={followUpFormData.next_follow_up_date}
              onChange={(e) => setFollowUpFormData({ ...followUpFormData, next_follow_up_date: e.target.value })}
            />
          </div>
        </div>

        <div className="modal-footer">
          <button
            className="btn btn--outline"
            onClick={() => setShowFollowUpModal(false)}
          >
            <FiX size={18} />
            <span>Cancelar</span>
          </button>
          <button
            className="btn btn--primary"
            onClick={handleSaveFollowUp}
            disabled={!followUpFormData.title}
          >
            <FiCheck size={18} />
            <span>Programar Seguimiento</span>
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default Prospeccion;
